"use client";
import React, { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  motion,
  useInView,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import Picture from "../public/assets/abubokor.jpeg";
import { Element } from "react-scroll";

/* ─────────────────────────────────────────
   ANIMATION VARIANTS
───────────────────────────────────────── */
const fadeUp = {
  hidden: { y: 50, opacity: 0 },
  show: (d = 0) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.75, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeLeft = {
  hidden: { x: -60, opacity: 0 },
  show: (d = 0) => ({
    x: 0,
    opacity: 1,
    transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeRight = {
  hidden: { x: 60, opacity: 0 },
  show: (d = 0) => ({
    x: 0,
    opacity: 1,
    transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const itemVariant = {
  hidden: { y: 28, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

/* ─────────────────────────────────────────
   STAT CARD
───────────────────────────────────────── */
function StatCard({ value, label, delay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={ref}
      custom={delay}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      className="relative flex flex-col items-center justify-center p-6 rounded-2xl
        border border-white/[0.07] bg-white/[0.02] backdrop-blur-sm
        hover:border-[#00f0ff]/30 hover:bg-[#00f0ff]/[0.04]
        transition-colors duration-400 group overflow-hidden"
    >
      {/* Corner accent */}
      <span
        className="absolute top-0 left-0 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: "linear-gradient(135deg, #00f0ff40, transparent 60%)",
          borderRadius: "0 0 100% 0",
        }}
      />
      <span
        className="text-4xl md:text-5xl font-black text-white"
        style={{ fontFamily: "'Syne', sans-serif" }}
      >
        {value}
      </span>
      <span
        className="text-[10px] md:text-xs text-white/35 tracking-[0.25em] uppercase mt-1"
        style={{ fontFamily: "'DM Mono', monospace" }}
      >
        {label}
      </span>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   SKILL BADGE
───────────────────────────────────────── */
function SkillBadge({ skill, index }) {
  return (
    <motion.div
      variants={itemVariant}
      whileHover={{ scale: 1.08, y: -3 }}
      whileTap={{ scale: 0.95 }}
      className="group flex flex-col items-center gap-2 p-3 rounded-xl
        border border-white/[0.06] bg-white/[0.02]
        hover:border-[#00f0ff]/40 hover:bg-[#00f0ff]/[0.05]
        transition-colors duration-300 cursor-default"
    >
      <div
        className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center p-2
        group-hover:bg-white/10 transition-colors duration-300"
      >
        <img
          src={skill.icon}
          alt={skill.technology_name}
          className="w-full h-full object-contain"
        />
      </div>
      <span
        className="text-[9px] text-white/30 group-hover:text-[#00f0ff]/70 tracking-[0.15em] uppercase transition-colors duration-300"
        style={{ fontFamily: "'DM Mono', monospace" }}
      >
        {skill.technology_name}
      </span>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   ROLE TAG
───────────────────────────────────────── */
function RoleTag({ icon, label, delay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      custom={delay}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full
        border border-[#00f0ff]/15 bg-[#00f0ff]/[0.04]"
    >
      <span className="text-[#00f0ff] text-sm">{icon}</span>
      <span
        className="text-xs text-white/55 tracking-[0.12em]"
        style={{ fontFamily: "'DM Mono', monospace" }}
      >
        {label}
      </span>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   PROFILE IMAGE with tilt
───────────────────────────────────────── */
function ProfileImage() {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), {
    stiffness: 100,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), {
    stiffness: 100,
    damping: 20,
  });

  const handleMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };

  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={sectionRef}
      custom={0}
      variants={fadeLeft}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      className="relative flex-shrink-0 mx-auto lg:mx-0"
    >
      {/* Tilt wrapper */}
      <motion.div
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={reset}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          perspective: 800,
        }}
        className="relative w-72 h-80 md:w-80 md:h-96 cursor-none"
      >
        {/* Outer glow ring */}
        <div
          className="absolute inset-0 rounded-2xl"
          style={{
            background:
              "linear-gradient(135deg, rgba(0,71,255,0.35), rgba(0,240,255,0.2), transparent)",
            padding: "1.5px",
          }}
        >
          <div
            className="absolute inset-0 rounded-2xl"
            style={{ background: "#05050b" }}
          />
        </div>

        {/* Image */}
        <div className="relative z-10 w-full h-full rounded-2xl overflow-hidden border border-white/[0.06]">
          <Image
            src={Picture}
            alt="Abu Bokor"
            fill
            className="object-cover object-top"
            sizes="(max-width: 768px) 288px, 320px"
          />
          {/* Overlay gradient */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(5,5,11,0.7) 0%, transparent 50%)",
            }}
          />
        </div>

        {/* Floating badge — experience */}
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.9, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute -bottom-4 -right-4 z-20 flex items-center gap-2.5
            px-4 py-2.5 rounded-xl
            border border-[#00f0ff]/25 bg-[#05050b]/90 backdrop-blur-md"
        >
          <span className="text-[#00f0ff] text-lg">⚡</span>
          <div>
            <div
              className="text-xs font-bold text-white"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              2+ Years
            </div>
            <div
              className="text-[9px] text-white/35 tracking-[0.15em] uppercase"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              Experience
            </div>
          </div>
        </motion.div>

        {/* Floating badge — backend */}
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 1.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute -top-4 -left-4 z-20 flex items-center gap-2
            px-3 py-2 rounded-xl
            border border-[#0047ff]/30 bg-[#05050b]/90 backdrop-blur-md"
        >
          <span className="text-[#0047ff] text-sm">◈</span>
          <span
            className="text-[9px] text-white/45 tracking-[0.15em] uppercase"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            Backend Focus
          </span>
        </motion.div>

        {/* Corner brackets */}
        <svg
          className="absolute top-2 left-2 opacity-40 z-20"
          width="20"
          height="20"
          fill="none"
        >
          <path d="M0 20 L0 0 L20 0" stroke="#00f0ff" strokeWidth="1.5" />
        </svg>
        <svg
          className="absolute bottom-2 right-2 opacity-40 z-20"
          width="20"
          height="20"
          fill="none"
        >
          <path d="M20 0 L20 20 L0 20" stroke="#0047ff" strokeWidth="1.5" />
        </svg>
      </motion.div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   MAIN ABOUT COMPONENT
───────────────────────────────────────── */
const About = ({ aboutData, skillData }) => {
  /* Section refs for inView triggers */
  const headRef = useRef(null);
  const contentRef = useRef(null);
  const statsRef = useRef(null);
  const skillsRef = useRef(null);

  const headInView = useInView(headRef, { once: true, margin: "-60px" });
  const contentInView = useInView(contentRef, { once: true, margin: "-80px" });
  const statsInView = useInView(statsRef, { once: true, margin: "-80px" });
  const skillsInView = useInView(skillsRef, { once: true, margin: "-80px" });

  /* Info grid data */
  const info = [
    { label: "Location", value: "Bangladesh", icon: "◎" },
    { label: "Email", value: "abubokor1066@gmail.com", icon: "◈" },
    { label: "Phone", value: "+8801885236058", icon: "◉" },
    { label: "Languages", value: "Bangla, English", icon: "◐" },
  ];

  /* Role tags */
  const roles = [
    { icon: "▸", label: "Full Stack Engineer" },
    { icon: "▸", label: "Backend Focused" },
    { icon: "▸", label: "Project Lead / Sub-Lead" },
    { icon: "▸", label: "Software Architect" },
  ];

  /* Stats */
  const stats = [
    { value: "2+", label: "Years Experience" },
    { value: "10+", label: "Projects Built" },
    { value: "5+", label: "Led Projects" },
    { value: "∞", label: "Lines of Code" },
  ];

  return (
    <Element name="about">
      <section
        className="relative overflow-hidden"
        style={{ background: "#05050b" }}
      >
        {/* ── Subtle grid background ── */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.035]"
          aria-hidden
        >
          <defs>
            <pattern
              id="about-grid"
              width="72"
              height="72"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 72 0 L 0 0 0 72"
                fill="none"
                stroke="white"
                strokeWidth="0.4"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#about-grid)" />
        </svg>

        {/* ── Background accent orbs ── */}
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] pointer-events-none"
          aria-hidden
        >
          <div
            style={{
              background:
                "radial-gradient(circle, rgba(0,71,255,0.12) 0%, transparent 70%)",
              filter: "blur(80px)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>
        <div
          className="absolute bottom-0 left-0 w-[400px] h-[400px] pointer-events-none"
          aria-hidden
        >
          <div
            style={{
              background:
                "radial-gradient(circle, rgba(0,240,255,0.08) 0%, transparent 70%)",
              filter: "blur(80px)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 py-10 ">
          {/* ── SECTION HEADING ── */}
          <div ref={headRef} className="mb-16 lg:mb-20">
            {/* Label */}
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate={headInView ? "show" : "hidden"}
              className="flex items-center gap-4 mb-5"
            >
              <div className="h-px w-12 bg-gradient-to-r from-[#0047ff] to-[#00f0ff]" />
              <span
                className="text-[10px] tracking-[0.32em] text-[#00f0ff]/60 uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                02 — About
              </span>
            </motion.div>

            {/* Heading */}
            <div className="overflow-hidden">
              <motion.h2
                custom={0.1}
                variants={fadeUp}
                initial="hidden"
                animate={headInView ? "show" : "hidden"}
                className="text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.88] tracking-tight text-white"
                style={{ fontFamily: "'Syne', sans-serif" }}
              >
                WHO I
              </motion.h2>
            </div>
            <div className="overflow-hidden">
              <motion.h2
                custom={0.2}
                variants={fadeUp}
                initial="hidden"
                animate={headInView ? "show" : "hidden"}
                className="text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.88] tracking-tight"
                style={{
                  fontFamily: "'Syne', sans-serif",
                  WebkitTextStroke: "1.5px rgba(0,240,255,0.35)",
                  color: "transparent",
                }}
              >
                AM.
              </motion.h2>
            </div>

            {/* Gradient rule */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={headInView ? { scaleX: 1 } : {}}
              transition={{
                duration: 1,
                delay: 0.35,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="mt-6 h-px w-40 origin-left"
              style={{
                background:
                  "linear-gradient(90deg, #0047ff, #00f0ff60, transparent)",
              }}
            />
          </div>

          {/* ── MAIN TWO-COLUMN ── */}
          <div
            ref={contentRef}
            className="flex flex-col lg:flex-row gap-14 lg:gap-20 items-start mb-20 lg:mb-28"
          >
            {/* Left — profile image */}
            <ProfileImage />

            {/* Right — bio content */}
            <div className="flex-1 min-w-0">
              {/* Role tags row */}
              <motion.div
                initial="hidden"
                animate={contentInView ? "show" : "hidden"}
                variants={staggerContainer}
                className="flex flex-wrap gap-2 mb-8"
              >
                {roles.map((r, i) => (
                  <motion.div
                    key={r.label}
                    variants={itemVariant}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full
                      border border-[#00f0ff]/12 bg-[#00f0ff]/[0.03]"
                  >
                    <span className="text-[#00f0ff] text-xs">{r.icon}</span>
                    <span
                      className="text-[10px] text-white/50 tracking-[0.15em] uppercase"
                      style={{ fontFamily: "'DM Mono', monospace" }}
                    >
                      {r.label}
                    </span>
                  </motion.div>
                ))}
              </motion.div>

              {/* Bio paragraphs */}
              <motion.div
                custom={0.1}
                variants={fadeRight}
                initial="hidden"
                animate={contentInView ? "show" : "hidden"}
                className="space-y-5 mb-8"
              >
                <p
                  className="text-base md:text-lg text-white/60 leading-relaxed"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  I'm a{" "}
                  <span className="text-white font-semibold">
                    Full Stack Engineer
                  </span>{" "}
                  with around 2 years of experience, working on real-world
                  production systems. I focus on building reliable backend
                  services while also delivering complete frontend experiences
                  when needed.
                </p>

                <p
                  className="text-base md:text-lg text-white/55 leading-relaxed"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  I work with the{" "}
                  <span className="text-white/80">
                    JavaScript/TypeScript ecosystem and modern tools
                  </span>{" "}
                  like Next.js, Nest.js, and PostgreSQL. I’ve contributed to
                  full-stack applications including a CRM system and a
                  short-video platform, handling APIs, authentication, database
                  design, and performance optimization. I also managed the
                  mobile app development lifecycle — from implementation to
                  successfully publishing it on app stores.
                </p>

                <p
                  className="text-base text-white/40 leading-relaxed"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  I take ownership of features from planning to deployment and
                  have supported teams by mentoring interns and improving
                  development workflows. I focus on writing maintainable code,
                  solving practical problems, and continuously improving my
                  skills.
                </p>
              </motion.div>

              {/* Info grid */}
              <motion.div
                custom={0.25}
                variants={fadeRight}
                initial="hidden"
                animate={contentInView ? "show" : "hidden"}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10"
              >
                {info.map(({ label, value, icon }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 py-3 px-4 rounded-lg
                      border border-white/[0.05] bg-white/[0.015]"
                  >
                    <span className="text-[#00f0ff]/50 text-sm flex-shrink-0">
                      {icon}
                    </span>
                    <div>
                      <div
                        className="text-[9px] tracking-[0.22em] text-white/25 uppercase mb-0.5"
                        style={{ fontFamily: "'DM Mono', monospace" }}
                      >
                        {label}
                      </div>
                      <div
                        className="text-sm text-white/70"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {value}
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* CTA buttons */}
              <motion.div
                custom={0.38}
                variants={fadeRight}
                initial="hidden"
                animate={contentInView ? "show" : "hidden"}
                className="flex flex-wrap gap-4"
              >
                <motion.div
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                >
                  <Link
                    href="/#contact"
                    className="relative group inline-flex items-center gap-3
                      px-7 py-3.5 rounded-full font-semibold text-sm tracking-[0.18em] uppercase
                      text-[#05050b] overflow-hidden"
                    style={{
                      background:
                        "linear-gradient(135deg, #00f0ff 0%, #0047ff 100%)",
                      boxShadow: "0 0 28px rgba(0,240,255,0.22)",
                      fontFamily: "'DM Mono', monospace",
                    }}
                  >
                    <span className="relative z-10">Let's Connect</span>
                    <motion.span
                      className="relative z-10"
                      animate={{ x: [0, 4, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      →
                    </motion.span>
                    <span
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.18), transparent)",
                      }}
                    />
                  </Link>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                >
                  <a
                    href="/resume.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3
                      px-7 py-3.5 rounded-full font-semibold text-sm tracking-[0.18em] uppercase
                      text-white/60 border border-white/[0.09]
                      hover:border-[#00f0ff]/35 hover:text-[#00f0ff] hover:bg-[#00f0ff]/[0.05]
                      transition-all duration-300 backdrop-blur-sm"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    Download CV
                    <span className="text-base">↓</span>
                  </a>
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* ── STATS ROW ── */}
          <div ref={statsRef} className="mb-20 lg:mb-28">
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate={statsInView ? "show" : "hidden"}
              className="flex items-center gap-4 mb-8"
            >
              <div className="h-px w-8 bg-[#0047ff]" />
              <span
                className="text-[10px] tracking-[0.28em] text-white/25 uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                By the numbers
              </span>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((s, i) => (
                <StatCard
                  key={s.label}
                  value={s.value}
                  label={s.label}
                  delay={i * 0.08}
                />
              ))}
            </div>
          </div>

          {/* ── SKILLS GRID ── */}
          {skillData?.length > 0 && (
            <div ref={skillsRef}>
              {/* Label */}
              <motion.div
                custom={0}
                variants={fadeUp}
                initial="hidden"
                animate={skillsInView ? "show" : "hidden"}
                className="flex items-center justify-between mb-8"
              >
                <div className="flex items-center gap-4">
                  <div className="h-px w-8 bg-[#00f0ff]" />
                  <span
                    className="text-[10px] tracking-[0.28em] text-white/25 uppercase"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    Tech Stack
                  </span>
                </div>
                <span
                  className="text-[10px] text-white/20 tracking-[0.15em]"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  {skillData.length} technologies
                </span>
              </motion.div>

              {/* Full-width top divider */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={skillsInView ? { scaleX: 1 } : {}}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="h-px w-full origin-left mb-8"
                style={{
                  background:
                    "linear-gradient(90deg, #0047ff40, #00f0ff20, transparent)",
                }}
              />

              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate={skillsInView ? "show" : "hidden"}
                className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-3"
              >
                {skillData.map((skill, i) => (
                  <SkillBadge key={skill._id} skill={skill} index={i} />
                ))}
              </motion.div>

              {/* Bottom divider */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={skillsInView ? { scaleX: 1 } : {}}
                transition={{
                  duration: 0.9,
                  delay: 0.3,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="h-px w-full origin-right mt-8"
                style={{
                  background:
                    "linear-gradient(270deg, #0047ff40, #00f0ff20, transparent)",
                }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Mono:ital,wght@0,300;0,400;0,500&family=DM+Sans:wght@300;400;500&display=swap');
      `}</style>
    </Element>
  );
};

export default About;
